import { useEffect, useMemo, useState } from 'react';
import {
  ActionsDialogue, BarreOutils, Bouton, Carte, CaseACocher, Champ, Dialogue,
  EnTetePage, Espaceur, GroupeBoutons, IconeTelecharger, Jauge, Message, Selecteur,
  SqueletteTableau, ZoneTexte, couleurDonnees, useTheme,
} from '@journal/ui';
import { useDonnees, useEcriture } from '../lib/donnees.ts';
import { useSession } from '../lib/session.tsx';
import { useTelechargementPdf } from '../lib/impression.tsx';
import { useAnneeFacultative } from '../lib/annee.tsx';
import { demandeV1 } from '../lib/impression.ts';
import { titreV1 } from '../lib/gabarits-v1.ts';
import { documentHeuresV1 } from './impressions/sections.ts';
import { lundiDe } from './Progressions.tsx';
import { decalerJour } from './Journal.tsx';

/**
 * Les 108 heures.
 *
 * ── Ce que l'écran compte ───────────────────────────────────────────────────
 * Les heures dues au-delà des vingt-quatre heures d'enseignement : APC, travail
 * en équipe, formations, conseils d'école. L'obligation est INDIVIDUELLE — ce
 * décompte est celui de l'enseignant connecté, jamais celui de l'école.
 *
 * ── Un calendrier, et non un tableau ────────────────────────────────────────
 * Une liste de lignes datées serait plus simple à écrire. Mais on ne saisit pas
 * ses heures en les listant : on se souvient d'un mardi, et l'on cherche le
 * mardi. Le calendrier est aussi ce qui fait voir les trous — une semaine vide
 * se remarque, une ligne absente d'un tableau non.
 *
 * ── Le récapitulatif ne bouge JAMAIS ────────────────────────────────────────
 * Il porte sur l'année scolaire active, et sur elle seule. La v1 laissait la
 * navigation dans le calendrier changer l'année suivie en franchissant le
 * 1er septembre : les compteurs de droite variaient alors d'un mois à l'autre,
 * sans que rien ne l'explique. Naviguer déplace le calendrier, et rien d'autre.
 */

// ── Catégories ───────────────────────────────────────────────────────────────
/*
 * Les objectifs ne figurent PAS ici : ils viennent de la base, donc réglables
 * si la réglementation évolue. Elle a déjà évolué — la v1 comptait 60 h d'APC.
 *
 * Six catégories de saisie pour quatre compteurs : « Formation continue » et
 * « Animation pédagogique » se distinguent quand on note, et se cumulent quand
 * on compte, parce que le texte les plafonne ensemble.
 */
/*
 * ── La couleur n'est plus écrite ici ────────────────────────────────────────
 * Les six teintes étaient posées en dur, et redéclarées une seconde fois plus
 * bas pour le récapitulatif : changer la couleur d'une catégorie dans la liste
 * laissait le graphique en montrer une autre. Chacune désigne maintenant son
 * RANG dans la palette de données, résolue selon le thème en cours.
 *
 * Deux d'entre elles étaient par ailleurs trop pâles pour un thème clair : la
 * pastille verte d'« Équipe » tombait à 2,54:1 sur blanc et celle de
 * « Formation » à 2,15, sous le seuil de 3:1 que WCAG demande à un élément
 * graphique porteur de sens. La palette les remonte au-dessus de 4,9.
 */
export const CATEGORIES = [
  { cle: 'apc', nom: 'APC', long: 'Activités pédagogiques complémentaires', rang: 0 },
  { cle: 'equipe', nom: 'Équipe', long: 'RDV parents, conseils de maîtres et de cycles', rang: 2 },
  { cle: 'formation', nom: 'Formation', long: 'Formation continue', rang: 3 },
  { cle: 'animation', nom: 'Animation', long: 'Animation pédagogique', rang: 6 },
  { cle: 'conseil', nom: 'Conseil', long: 'Conseil d’école', rang: 1 },
  { cle: 'autre', nom: 'Autre', long: 'Autre', rang: 7 },
] as const;

/** Rang d'une catégorie dans la palette, par sa clé. */
export function rangCategorie(cle: string): number {
  return CATEGORIES.find((c) => c.cle === cle)?.rang ?? 7;
}

export type CleCategorie = (typeof CATEGORIES)[number]['cle'];

/** Une catégorie inconnue retombe sur « Autre » plutôt que de rendre undefined. */
export const categorie = (cle: string) =>
  CATEGORIES.find((c) => c.cle === cle) ?? CATEGORIES[CATEGORIES.length - 1]!;

const MODALITES = ['Présentiel', 'Distanciel', 'Hybride', 'Autre'];

export interface Activite {
  id: string;
  date: string;
  category: CleCategorie;
  minutes: number;
  title: string;
  modality: string;
  description: string;
  seriesId: string | null;
}

export interface Objectifs {
  apc: number;
  equipe: number;
  formation_animation: number;
  conseil: number;
  total: number;
}

interface Chargement {
  schoolYearId: string;
  activites: Activite[];
  plageRecurrence: { debut: string; fin: string };
}

// ── Durées ───────────────────────────────────────────────────────────────────

/**
 * Minutes → « 6 h 30 », « 45 min », « 3 h ».
 *
 * Jamais en décimal : « 6,5 h » se lit mal et invite à saisir « 6,3 » pour six
 * heures trente. Les heures de service se comptent en heures et minutes.
 */
export function formaterDuree(minutes: number): string {
  const m = Math.max(0, Math.round(minutes || 0));
  const heures = Math.floor(m / 60);
  const reste = m % 60;
  if (!heures) return `${reste} min`;
  return reste ? `${heures} h ${String(reste).padStart(2, '0')}` : `${heures} h`;
}

/**
 * Les quatre compteurs réglementaires, à partir des activités saisies.
 *
 * Quatre, pas six : « Formations » cumule la formation continue et l'animation
 * pédagogique, que le texte plafonne ensemble. Et « Autre » n'a pas de
 * compteur — il entre dans le total sans avoir d'objectif propre, ce qui est
 * exactement ce qu'on attend d'un fourre-tout.
 */
export function compteurs(activites: Activite[], objectifs: Objectifs) {
  const parCategorie = new Map<string, number>();
  for (const a of activites) {
    parCategorie.set(a.category, (parCategorie.get(a.category) ?? 0) + a.minutes);
  }
  const min = (cle: string) => parCategorie.get(cle) ?? 0;
  const heures = (n: number) => Math.round((Number(n) || 0) * 60);

  return {
    total: [...parCategorie.values()].reduce((s, v) => s + v, 0),
    cibleTotal: heures(objectifs.total),
    // Le RANG de la catégorie, et non sa couleur : le récapitulatif redéclarait
    // les teintes, si bien qu'il pouvait montrer autre chose que le calendrier.
    postes: [
      { nom: 'APC', fait: min('apc'), cible: heures(objectifs.apc), rang: rangCategorie('apc') },
      {
        nom: 'RDV parents, conseils',
        fait: min('equipe'),
        cible: heures(objectifs.equipe),
        rang: rangCategorie('equipe'),
      },
      {
        nom: 'Formations',
        fait: min('formation') + min('animation'),
        cible: heures(objectifs.formation_animation),
        // Le poste réunit formation continue et animation pédagogique : il
        // prend le rang de la première, qui pèse le plus lourd.
        rang: rangCategorie('formation'),
      },
      {
        nom: 'Conseils d’école',
        fait: min('conseil'),
        cible: heures(objectifs.conseil),
        rang: rangCategorie('conseil'),
      },
    ],
  };
}

// ── Calendrier ───────────────────────────────────────────────────────────────

/**
 * Les jours d'une grille mensuelle, semaines entières comprises.
 *
 * La grille commence au lundi précédant le 1er et s'arrête à la fin de la
 * semaine contenant le dernier jour : sans cela, un mois commençant un
 * dimanche perdrait sa première ligne. Les jours des mois voisins sont rendus
 * grisés — les masquer laisserait des trous où le regard cherche une case.
 */
export function grilleMois(mois: string): string[] {
  const cle = mois.slice(0, 7);
  const debut = lundiDe(`${cle}-01`);

  /*
   * On retire les SEMAINES entièrement hors du mois, jamais des jours isolés.
   *
   * La v1 s'arrêtait « dès qu'un jour d'indice ≥ 35 sort du mois ». Pour un
   * mois qui finit le lundi de la sixième semaine — mars 2026, par exemple —
   * la coupure tombait au mardi : la grille comptait 36 jours, et la dernière
   * ligne s'affichait avec une case et six trous.
   *
   * Six lignes ne servent qu'aux mois qui débordent vraiment ; les autres en
   * ont cinq, entières.
   */
  const semaines: string[][] = [];
  for (let s = 0; s < 6; s++) {
    const semaine = Array.from({ length: 7 }, (_, j) => decalerJour(debut, s * 7 + j));
    if (s >= 4 && semaine.every((d) => d.slice(0, 7) !== cle)) break;
    semaines.push(semaine);
  }
  return semaines.flat();
}

const aujourdhui = () => new Date().toISOString().slice(0, 10);

const moisSuivant = (iso: string, sens: number) => {
  const [a, m] = iso.split('-').map(Number);
  const d = new Date(Date.UTC(a!, m! - 1 + sens, 1));
  return d.toISOString().slice(0, 10);
};

const libelleMois = (iso: string) => {
  const s = new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', {
    month: 'long', year: 'numeric', timeZone: 'UTC',
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const libelleJourLong = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC',
  });

const JOURS_COURTS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// ═══════════════════════════════════════════════════════════════════════════

export function HeuresService() {
  const { api } = useSession();
  const { theme } = useTheme();
  const [vue, setVue] = useState<'mois' | 'semaine'>('mois');
  const [filtre, setFiltre] = useState<'toutes' | CleCategorie>('toutes');
  const [curseur, setCurseur] = useState<string | null>(null);

  const [saisie, setSaisie] = useState<{ date: string; activite: Activite | null } | null>(null);
  const [recurrence, setRecurrence] = useState(false);
  const [reglageObjectifs, setReglageObjectifs] = useState(false);

  const donnees = useDonnees<Chargement>('/service-hours');
  const objectifs = useDonnees<Objectifs>('/service-hours/objectives');
  const { executer, erreur: erreurEcriture } = useEcriture();

  /*
   * ── Où s'ouvre le calendrier ──────────────────────────────────────────────
   * Sur aujourd'hui si l'on est dans la période d'enseignement, sinon au début
   * de la période utile — c'est exactement ce que le serveur calcule déjà pour
   * proposer la plage d'une récurrence, et le réutiliser évite d'avoir deux
   * réponses différentes à la même question.
   *
   * La v1 s'ouvrait sur le mois courant : consulté pendant les vacances d'été,
   * le module affichait une grille vide et paraissait cassé.
   */
  useEffect(() => {
    if (curseur !== null || !donnees.donnees) return;
    // La plage est le mois en cours, ramené dans l'enseignement : aujourd'hui
    // s'il en fait partie, sinon son premier jour (avant la rentrée, l'été).
    const { debut, fin } = donnees.donnees.plageRecurrence;
    const aujourdhui = new Date().toLocaleDateString('sv-SE');
    setCurseur(aujourdhui >= debut && aujourdhui <= fin ? aujourdhui : debut);
  }, [curseur, donnees.donnees]);

  const activites = donnees.donnees?.activites ?? [];

  /*
   * Impression : un document construit à partir des données, comme dans la
   * v1 — récapitulatif, puis le détail de tous les créneaux de l'année. Le
   * même que dans le dossier du module Impressions. Jamais `print()` sur
   * l'application : l'aperçu la figeait tant qu'il restait ouvert.
   */
  const anneeActive = useAnneeFacultative();
  const pdf = useTelechargementPdf();
  const impression = {
    erreur: pdf.erreur,
    enCours: pdf.enCours,
    lancer: () => {
      if (!objectifs.donnees) return;
      // Le modèle de la v1 : « 108 heures — Année 2026-2027 ».
      void pdf.telecharger(demandeV1(documentHeuresV1(
        titreV1('108 heures', anneeActive ? `Année ${anneeActive.label}` : ''), activites, objectifs.donnees,
      )));
    },
  };
  const visibles = useMemo(
    () => activites.filter((a) => filtre === 'toutes' || a.category === filtre),
    [activites, filtre],
  );

  const parJour = useMemo(() => {
    const index = new Map<string, Activite[]>();
    for (const a of visibles) {
      const liste = index.get(a.date);
      if (liste) liste.push(a); else index.set(a.date, [a]);
    }
    return index;
  }, [visibles]);

  const bilan = useMemo(
    // Les compteurs portent sur TOUTES les activités, jamais sur le filtre :
    // masquer une catégorie à l'écran ne la retire pas de l'obligation.
    () => compteurs(activites, objectifs.donnees ?? {
      apc: 36, equipe: 48, formation_animation: 18, conseil: 6, total: 108,
    }),
    [activites, objectifs.donnees],
  );

  const rafraichir = async () => { await donnees.recharger(); };

  if (donnees.chargement && !donnees.donnees) return <SqueletteTableau lignes={6} />;
  if (donnees.erreur) {
    return (
      <Message ton="erreur" titre="Chargement impossible">
        {donnees.statutErreur === 404
          ? 'Créez d’abord une année scolaire dans les Réglages.'
          : donnees.erreur}
      </Message>
    );
  }

  const position = curseur ?? aujourdhui();
  const jours = vue === 'mois'
    ? grilleMois(position)
    : Array.from({ length: 7 }, (_, i) => decalerJour(lundiDe(position), i));

  return (
    <div className="flex flex-col gap-4">
      {erreurEcriture && <Message ton="erreur">{erreurEcriture}</Message>}
      {impression.erreur && <Message ton="erreur">{impression.erreur}</Message>}

      <EnTetePage
        titre="108 heures"
        contexte={`${formaterDuree(bilan.total)} sur ${formaterDuree(bilan.cibleTotal)}`}
        actions={
          <>
            <Bouton variante="ghost" onClick={() => setReglageObjectifs(true)}>
              Objectifs
            </Bouton>
            <Bouton
              variante="ghost"
              icone={<IconeTelecharger taille={16} />}
              chargement={impression.enCours}
              onClick={impression.lancer}
            >
              Télécharger le PDF
            </Bouton>
          </>
        }
      />

      <BarreOutils>
        {/*
          * « Période » écrit, comme « Catégorie » à côté.
          *
          * La bascule était nue : la barre montrait un champ libellé d'un côté
          * et deux boutons sans titre de l'autre, sans dire ce qu'ils
          * choisissaient.
          */}
        <GroupeBoutons
          legende="Période"
          legendeVisible
          valeur={vue}
          surChangement={setVue}
          options={[
            { valeur: 'mois', texte: 'Mois' },
            { valeur: 'semaine', texte: 'Semaine' },
          ]}
        />
        <Selecteur
          libelle="Catégorie"
          value={filtre}
          onChange={(e) => setFiltre(e.target.value as 'toutes' | CleCategorie)}
          options={[
            { valeur: 'toutes', texte: 'Toutes' },
            ...CATEGORIES.map((c) => ({ valeur: c.cle, texte: c.long })),
          ]}
        />
        <Espaceur />
        {/*
          * Saisir une activité récurrente est un geste de CETTE barre : on y
          * choisit ce qu'on regarde, et c'est là qu'on ajoute. Le bouton était
          * en tête de page, à côté d'« Imprimer », c'est-à-dire parmi les
          * actions qui portent sur la page entière.
          */}
        <Bouton variante="secondary" onClick={() => setRecurrence(true)}>
          Activité récurrente
        </Bouton>
      </BarreOutils>

      <div className="grid gap-4 xl:grid-cols-[1fr_20rem] print:hidden">
        <Carte
          titre={vue === 'mois'
            ? libelleMois(position)
            : `Semaine du ${libelleJourLong(lundiDe(position))}`}
          action={
            <div className="flex items-center gap-1">
              <Bouton
                variante="ghost" taille="sm"
                onClick={() => setCurseur(vue === 'mois'
                  ? moisSuivant(position, -1)
                  : decalerJour(position, -7))}
                aria-label="Période précédente"
              >
                ‹
              </Bouton>
              <Bouton variante="ghost" taille="sm" onClick={() => setCurseur(aujourdhui())}>
                Aujourd’hui
              </Bouton>
              <Bouton
                variante="ghost" taille="sm"
                onClick={() => setCurseur(vue === 'mois'
                  ? moisSuivant(position, 1)
                  : decalerJour(position, 7))}
                aria-label="Période suivante"
              >
                ›
              </Bouton>
            </div>
          }
        >
          {vue === 'mois' && (
            <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[11px] font-medium uppercase tracking-wide text-[var(--color-fg-muted)]">
              {JOURS_COURTS.map((j) => <div key={j}>{j}</div>)}
            </div>
          )}
          <div className={vue === 'mois' ? 'grid grid-cols-7 gap-1' : 'flex flex-col gap-1'}>
            {jours.map((jour) => (
              <CaseJour
                key={jour}
                jour={jour}
                mois={position.slice(0, 7)}
                semaine={vue === 'semaine'}
                activites={parJour.get(jour) ?? []}
                surAjout={() => setSaisie({ date: jour, activite: null })}
                surActivite={(a) => setSaisie({ date: a.date, activite: a })}
              />
            ))}
          </div>
        </Carte>

        <Carte titre="Récapitulatif annuel">
          <div className="flex flex-col gap-4">
            <Jauge
              titre="Total annuel"
              valeur={formaterDuree(bilan.total)}
              cible={formaterDuree(bilan.cibleTotal)}
              pourcentage={bilan.cibleTotal ? (bilan.total / bilan.cibleTotal) * 100 : 0}
              atteint={bilan.cibleTotal > 0 && bilan.total >= bilan.cibleTotal}
              libelle={reste(bilan.total, bilan.cibleTotal)}
            />
            <div className="h-px bg-[var(--color-border)]" />
            {bilan.postes.map((p) => (
              <Jauge
                key={p.nom}
                titre={p.nom}
                valeur={formaterDuree(p.fait)}
                cible={formaterDuree(p.cible)}
                couleur={couleurDonnees(p.rang, theme)}
                pourcentage={p.cible ? (p.fait / p.cible) * 100 : 0}
                atteint={p.cible > 0 && p.fait >= p.cible}
                libelle={reste(p.fait, p.cible)}
              />
            ))}
          </div>
        </Carte>
      </div>

      {/*
        * ── Ce qui part à l'imprimante ────────────────────────────────────────
        *
        * Pas le calendrier. Une grille mensuelle imprimée donne six lignes de
        * cases dont la plupart sont vides, et il en faut douze pour couvrir
        * l'année — alors que la question posée à ce document est « où en suis-je
        * de mes 108 heures, et de quoi sont-elles faites ». La v1 avait tranché
        * ainsi : un récapitulatif des quatre objectifs, puis le détail de tous
        * les créneaux, classés par date.
        *
        * `hidden print:block` : ces tableaux n'existent qu'à l'impression. Les
        * calculer à part risquerait de les faire diverger de l'écran — ils
        * partent donc des mêmes `bilan` et `activites`.
        */}
      {/*
        * `aria-hidden` : ces tableaux redisent, pour le papier, ce que le
        * calendrier et les compteurs montrent déjà. Les laisser dans l'arbre
        * d'accessibilité ferait entendre deux fois chaque activité à qui
        * navigue à la synthèse vocale.
        */}
      <div aria-hidden="true" className="hidden print:block">
        <h2 className="mb-2 text-[12pt] font-semibold">Récapitulatif</h2>
        <table className="mb-6 w-full border-collapse text-[10pt]">
          <thead>
            <tr>
              <th className="border-b border-[var(--color-border)] py-1 text-left">Objectif</th>
              <th className="border-b border-[var(--color-border)] py-1 text-right">Effectué</th>
              <th className="border-b border-[var(--color-border)] py-1 text-right">Cible</th>
              <th className="border-b border-[var(--color-border)] py-1 text-right">Restant</th>
            </tr>
          </thead>
          <tbody>
            {bilan.postes.map((p) => (
              <tr key={p.nom}>
                <td className="border-b border-[var(--color-border)] py-1">{p.nom}</td>
                <td className="border-b border-[var(--color-border)] py-1 text-right">
                  {formaterDuree(p.fait)}
                </td>
                <td className="border-b border-[var(--color-border)] py-1 text-right">
                  {formaterDuree(p.cible)}
                </td>
                <td className="border-b border-[var(--color-border)] py-1 text-right">
                  {p.fait >= p.cible ? 'atteint' : formaterDuree(p.cible - p.fait)}
                </td>
              </tr>
            ))}
            <tr className="font-bold">
              <td className="py-1">Total</td>
              <td className="py-1 text-right">{formaterDuree(bilan.total)}</td>
              <td className="py-1 text-right">{formaterDuree(bilan.cibleTotal)}</td>
              <td className="py-1 text-right">
                {bilan.total >= bilan.cibleTotal
                  ? 'atteint'
                  : formaterDuree(bilan.cibleTotal - bilan.total)}
              </td>
            </tr>
          </tbody>
        </table>

        <h2 className="mb-2 text-[12pt] font-semibold">
          {`Détail des créneaux (${activites.length})`}
        </h2>
        <table className="w-full border-collapse text-[10pt]">
          <thead>
            <tr>
              <th className="border-b border-[var(--color-border)] py-1 text-left">Date</th>
              <th className="border-b border-[var(--color-border)] py-1 text-left">Catégorie</th>
              <th className="border-b border-[var(--color-border)] py-1 text-right">Durée</th>
              <th className="border-b border-[var(--color-border)] py-1 text-left">Intitulé</th>
            </tr>
          </thead>
          <tbody>
            {[...activites]
              .sort((a, b) => a.date.localeCompare(b.date))
              .map((a) => (
                <tr key={a.id}>
                  <td className="border-b border-[var(--color-border)] py-1">
                    {libelleJourLong(a.date)}
                  </td>
                  <td className="border-b border-[var(--color-border)] py-1">
                    {categorie(a.category).long}
                  </td>
                  <td className="border-b border-[var(--color-border)] py-1 text-right">
                    {formaterDuree(a.minutes)}
                  </td>
                  <td className="border-b border-[var(--color-border)] py-1">{a.title}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {saisie && (
        <DialogueActivite
          date={saisie.date}
          activite={saisie.activite}
          surFermeture={() => setSaisie(null)}
          surEnregistrement={async (corps) => {
            const ok = await executer(() => (saisie.activite
              ? api.requete(`/service-hours/${saisie.activite.id}`, { methode: 'PATCH', corps })
              : api.requete('/service-hours', { methode: 'POST', corps })));
            if (ok) { setSaisie(null); await rafraichir(); }
          }}
          surSuppression={async (serie) => {
            const chemin = serie && saisie.activite?.seriesId
              ? `/service-hours/series/${saisie.activite.seriesId}`
              : `/service-hours/${saisie.activite!.id}`;
            const ok = await executer(() => api.requete(chemin, { methode: 'DELETE' }));
            if (ok) { setSaisie(null); await rafraichir(); }
          }}
        />
      )}

      {recurrence && donnees.donnees && (
        <DialogueRecurrence
          plage={donnees.donnees.plageRecurrence}
          surFermeture={() => setRecurrence(false)}
          surCreation={async (corps) => {
            const resultat = await api.requete<{
              crees: number; ignores: number; horsPeriode: number; premiere: string | null;
            }>('/service-hours/recurrence', { methode: 'POST', corps });
            if (resultat.crees > 0) {
              // Se placer sur la première séance : sans cela, une série créée
              // depuis un mois qu'elle ne couvre pas reste invisible, et l'on
              // croit qu'il ne s'est rien passé.
              if (resultat.premiere) { setCurseur(resultat.premiere); setVue('mois'); }
              setRecurrence(false);
              await rafraichir();
            }
            return resultat;
          }}
        />
      )}

      {reglageObjectifs && objectifs.donnees && (
        <DialogueObjectifs
          objectifs={objectifs.donnees}
          surFermeture={() => setReglageObjectifs(false)}
          surEnregistrement={async (corps) => {
            const ok = await executer(async () => {
              objectifs.definir(
                await api.requete<Objectifs>('/service-hours/objectives', {
                  methode: 'PUT', corps,
                }),
              );
            });
            if (ok) setReglageObjectifs(false);
          }}
        />
      )}
    </div>
  );
}

/** « 12 h 30 restantes », ou l'excédent quand l'objectif est dépassé. */
function reste(fait: number, cible: number): string {
  if (cible <= 0) return '';
  if (fait > cible) return `Objectif atteint : ${formaterDuree(fait - cible)} au-delà`;
  if (fait === cible) return 'Objectif atteint';
  return `${formaterDuree(cible - fait)} restantes`;
}

// ── Une case du calendrier ───────────────────────────────────────────────────

function CaseJour({
  jour, mois, semaine, activites, surAjout, surActivite,
}: {
  jour: string;
  mois: string;
  semaine: boolean;
  activites: Activite[];
  surAjout: () => void;
  surActivite: (a: Activite) => void;
}) {
  const { theme } = useTheme();
  const horsMois = !semaine && jour.slice(0, 7) !== mois;
  const estAujourdhui = jour === aujourdhui();
  const total = activites.reduce((s, a) => s + a.minutes, 0);
  const numero = Number(jour.slice(8, 10));

  return (
    <div
      /*
        * ── Toute la case ouvre la saisie ──────────────────────────────────
        *
        * Seul l'en-tête le faisait, et cliquer dans le vide d'une case — c'est
        *-à-dire presque partout — ne produisait rien. Un calendrier se clique
        * dans la case, pas sur son numéro.
        *
        * Le clic est relayé ici plutôt que posé sur un bouton englobant, deux
        * boutons ne pouvant être imbriqués et chaque activité en étant un. On
        * écarte donc les clics QUI ONT DÉJÀ UN DESTINATAIRE : l'en-tête, qui
        * fait le même travail, et les activités, pour lesquelles c'est la
        * modification qui prime — on vise ce qu'on voit.
        */
      onClick={(evenement) => {
        const cible = evenement.target as HTMLElement;
        if (cible.closest('button') === null) surAjout();
      }}
      className={[
        'flex cursor-pointer flex-col gap-1 rounded-lg border p-1.5 text-left',
        /*
         * ── Le survol porte sur la CASE, pas sur la date ────────────────────
         * L'en-tête portait le sien : survoler le haut d'une case y allumait un
         * fond derrière le numéro du jour, ce qui se lisait comme une date
         * sélectionnée alors que rien ne l'était. Or ce n'est pas la date qu'on
         * s'apprête à cliquer, c'est la journée.
         *
         * Un filet plus marqué et un fond à peine posé : assez pour désigner la
         * cible, trop peu pour concurrencer le jour d'aujourd'hui, qui garde son
         * anneau coloré.
         */
        'transition-colors duration-100',
        semaine ? 'min-h-[3.5rem]' : 'min-h-[5.5rem]',
        horsMois
          ? 'border-transparent bg-transparent text-[var(--color-fg-muted)] opacity-60 '
            + 'hover:border-[var(--color-border)]'
          : 'border-[var(--color-border)] bg-[var(--color-surface)] '
            + 'hover:border-[var(--color-border-strong)] hover:bg-[var(--color-surface-hover)]',
        estAujourdhui ? 'ring-1 ring-[var(--color-primary)]' : '',
      ].join(' ')}
    >
      {/*
        * L'en-tête reste un BOUTON, et chaque activité le sien.
        *
        * Le relais posé sur la case sert la souris ; il ne s'atteint pas au
        * clavier. L'en-tête porte donc l'ajout comme commande à part entière —
        * atteignable par tabulation, annoncée par un lecteur d'écran — et
        * chaque activité porte la sienne.
        */}
      <button
        type="button"
        onClick={surAjout}
        title={`Ajouter une activité le ${libelleJourLong(jour)}`}
        className={[
          'flex items-baseline justify-between gap-1 rounded px-0.5 text-[12px]',
          // Pas de survol propre : c'est la case entière qui s'allume. Le focus
          // clavier, lui, reste sur ce bouton — c'est par lui qu'on l'atteint.
          'focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]',
        ].join(' ')}
      >
        <span className={estAujourdhui ? 'font-semibold text-[var(--color-primary)]' : ''}>
          {semaine
            ? new Date(`${jour}T00:00:00Z`).toLocaleDateString('fr-FR', {
                weekday: 'short', day: 'numeric', timeZone: 'UTC',
              })
            : numero}
        </span>
        {/*
          * Le total du jour ne s'affiche qu'à partir de DEUX activités.
          *
          * Avec une seule, il répète mot pour mot la durée affichée juste en
          * dessous : ajouter un créneau d'une heure faisait apparaître « 1 h »
          * deux fois dans la même case, à trois millimètres d'écart. Une somme
          * d'un seul terme n'apprend rien — elle donne surtout à croire qu'on a
          * saisi deux fois.
          */}
        {activites.length > 1 && (
          <span className="tabular-nums text-[11px] text-[var(--color-fg-muted)]">
            {formaterDuree(total)}
          </span>
        )}
      </button>

      <div className="flex flex-col gap-0.5">
        {activites.map((a) => {
          const c = categorie(a.category);
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => surActivite(a)}
              title={`${c.long}${a.title ? ` · ${a.title}` : ''} · ${formaterDuree(a.minutes)}`}
              className={[
                'flex items-center gap-1 rounded px-1 py-0.5 text-left text-[11px]',
                'hover:bg-[var(--color-surface-hover)]',
                'focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]',
              ].join(' ')}
            >
              <span
                aria-hidden="true"
                className="size-1.5 shrink-0 rounded-full"
                style={{ background: couleurDonnees(c.rang, theme) }}
              />
              <span className="min-w-0 flex-1 truncate">{a.title || c.nom}</span>
              <span className="shrink-0 tabular-nums text-[var(--color-fg-muted)]">
                {formaterDuree(a.minutes)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Saisie d'une activité ────────────────────────────────────────────────────

interface CorpsActivite {
  date: string;
  category: CleCategorie;
  minutes: number;
  title: string;
  modality: string;
  description: string;
}

function DialogueActivite({
  date, activite, surFermeture, surEnregistrement, surSuppression,
}: {
  date: string;
  activite: Activite | null;
  surFermeture: () => void;
  surEnregistrement: (corps: CorpsActivite) => Promise<void>;
  surSuppression: (serie: boolean) => Promise<void>;
}) {
  const [valeurs, setValeurs] = useState({
    date: activite?.date ?? date,
    category: (activite?.category ?? 'apc') as CleCategorie,
    heures: activite ? Math.floor(activite.minutes / 60) : 1,
    minutes: activite ? activite.minutes % 60 : 0,
    title: activite?.title ?? '',
    modality: activite?.modality ?? '',
    description: activite?.description ?? '',
  });
  const [erreur, setErreur] = useState<string | null>(null);

  const total = valeurs.heures * 60 + valeurs.minutes;

  const enregistrer = async () => {
    if (!valeurs.date) return setErreur('Indiquez une date.');
    if (total <= 0) return setErreur('Indiquez une durée (heures et/ou minutes).');
    if (valeurs.minutes > 59) return setErreur('Les minutes vont de 0 à 59.');
    if (total > 720) return setErreur('Douze heures au maximum.');
    setErreur(null);
    await surEnregistrement({
      date: valeurs.date,
      category: valeurs.category,
      minutes: total,
      title: valeurs.title.trim(),
      modality: valeurs.modality,
      description: valeurs.description.trim(),
    });
  };

  return (
    <Dialogue
      ouvert
      titre={activite ? 'Modifier l’activité' : `Activité du ${libelleJourLong(date)}`}
      surFermeture={surFermeture}
    >
      <div className="flex flex-col gap-3">
        {erreur && <Message ton="erreur">{erreur}</Message>}

        <div className="grid gap-3 sm:grid-cols-2">
          <Champ
            libelle="Date"
            type="date"
            value={valeurs.date}
            onChange={(e) => setValeurs({ ...valeurs, date: e.target.value })}
          />
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium">Durée</span>
            <div className="flex items-center gap-1.5">
              <Champ
                type="number" min={0} max={12}
                libelle="Heures" libelleMasque
                value={String(valeurs.heures)}
                onChange={(e) => setValeurs({ ...valeurs, heures: Number(e.target.value) || 0 })}
              />
              <span className="text-sm text-[var(--color-fg-muted)]">h</span>
              <Champ
                type="number" min={0} max={59} step={5}
                libelle="Minutes" libelleMasque
                value={String(valeurs.minutes)}
                onChange={(e) => setValeurs({ ...valeurs, minutes: Number(e.target.value) || 0 })}
              />
              <span className="text-sm text-[var(--color-fg-muted)]">min</span>
            </div>
            {/* Les durées réelles, en un clic : 30 min pour une APC, 3 h pour
                une animation. Saisir deux nombres pour la même valeur trois
                fois par semaine finit par peser. */}
            <div className="flex flex-wrap gap-1">
              {[[0, 30], [0, 45], [1, 0], [1, 30], [2, 0], [3, 0]].map(([h, m]) => (
                <Bouton
                  key={`${h}-${m}`}
                  variante="ghost"
                  taille="sm"
                  onClick={() => setValeurs({ ...valeurs, heures: h!, minutes: m! })}
                >
                  {formaterDuree(h! * 60 + m!)}
                </Bouton>
              ))}
            </div>
          </div>
        </div>

        <Selecteur
          libelle="Catégorie"
          value={valeurs.category}
          onChange={(e) => setValeurs({ ...valeurs, category: e.target.value as CleCategorie })}
          options={CATEGORIES.map((c) => ({ valeur: c.cle, texte: c.long }))}
        />

        <Champ
          libelle="Intitulé"
          placeholder="Ex : APC lecture, conseil de cycle 2…"
          value={valeurs.title}
          onChange={(e) => setValeurs({ ...valeurs, title: e.target.value })}
        />

        <Selecteur
          libelle="Modalité"
          value={valeurs.modality}
          onChange={(e) => setValeurs({ ...valeurs, modality: e.target.value })}
          options={[
            { valeur: '', texte: '—' },
            ...MODALITES.map((m) => ({ valeur: m, texte: m })),
          ]}
        />

        <ZoneTexte
          libelle="Description"
          rows={2}
          value={valeurs.description}
          onChange={(e) => setValeurs({ ...valeurs, description: e.target.value })}
        />

        <ActionsDialogue>
          {activite && (
            <Bouton variante="danger" onClick={() => void surSuppression(false)}>
              Supprimer
            </Bouton>
          )}
          {activite?.seriesId && (
            <Bouton variante="danger" onClick={() => void surSuppression(true)}>
              Supprimer toute la série
            </Bouton>
          )}
          <Bouton variante="ghost" onClick={surFermeture}>Annuler</Bouton>
          <Bouton variante="primary" onClick={() => void enregistrer()}>Enregistrer</Bouton>
        </ActionsDialogue>
      </div>
    </Dialogue>
  );
}

// ── Activité récurrente ─────────────────────────────────────────────────────────

const JOURS_OUVRES = [
  { numero: 1, court: 'Lun' },
  { numero: 2, court: 'Mar' },
  { numero: 3, court: 'Mer' },
  { numero: 4, court: 'Jeu' },
  { numero: 5, court: 'Ven' },
];

function DialogueRecurrence({
  plage, surFermeture, surCreation,
}: {
  plage: { debut: string; fin: string };
  surFermeture: () => void;
  surCreation: (corps: Record<string, unknown>) => Promise<{
    crees: number; ignores: number; horsPeriode: number;
  }>;
}) {
  const [valeurs, setValeurs] = useState({
    debut: plage.debut,
    fin: plage.fin,
    // Lundi, mardi, jeudi, vendredi : les jours d'APC dans la semaine de
    // quatre jours, cas de très loin le plus fréquent.
    jours: [1, 2, 4, 5],
    category: 'apc' as CleCategorie,
    heures: 0,
    minutes: 30,
    title: '',
    modality: '',
    description: '',
    horsVacances: true,
  });
  const [message, setMessage] = useState<string | null>(null);

  const basculer = (numero: number) => setValeurs((v) => ({
    ...v,
    jours: v.jours.includes(numero)
      ? v.jours.filter((j) => j !== numero)
      : [...v.jours, numero].sort(),
  }));

  const creer = async () => {
    const total = valeurs.heures * 60 + valeurs.minutes;
    if (valeurs.fin < valeurs.debut) return setMessage('La date de fin précède la date de début.');
    if (!valeurs.jours.length) return setMessage('Choisissez au moins un jour de la semaine.');
    if (total <= 0) return setMessage('Indiquez une durée.');

    const r = await surCreation({
      debut: valeurs.debut,
      fin: valeurs.fin,
      jours: valeurs.jours,
      category: valeurs.category,
      minutes: total,
      title: valeurs.title.trim(),
      modality: valeurs.modality,
      description: valeurs.description.trim(),
      horsVacances: valeurs.horsVacances,
    });

    /*
     * Aucune activité créée : on GARDE la fenêtre ouverte, et l'on explique.
     *
     * La v1 se refermait sur un résultat vide, sans rien dire : l'enseignant
     * avait saisi une plage entièrement située dans les vacances, ne voyait
     * rien apparaître, et n'avait aucun moyen de comprendre pourquoi.
     */
    if (r.crees === 0) {
      if (r.horsPeriode > 0) {
        setMessage(
          `Aucune activité créée : les ${r.horsPeriode} dates de cette plage tombent `
          + 'toutes pendant les vacances. Ajustez les dates, ou décochez '
          + '« Ne rien créer pendant les vacances ».',
        );
      } else if (r.ignores > 0) {
        setMessage(`Rien de nouveau : les ${r.ignores} séances étaient déjà saisies.`);
      } else {
        setMessage('Aucune date ne correspond aux jours choisis sur cette plage.');
      }
    }
  };

  return (
    <Dialogue ouvert titre="Activité récurrente" largeur="lg" surFermeture={surFermeture}>
      <div className="flex flex-col gap-3">
        {message && <Message ton="attention">{message}</Message>}

        <div className="grid gap-3 sm:grid-cols-2">
          <Selecteur
            libelle="Catégorie"
            value={valeurs.category}
            onChange={(e) => setValeurs({ ...valeurs, category: e.target.value as CleCategorie })}
            options={CATEGORIES.map((c) => ({ valeur: c.cle, texte: c.long }))}
          />
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium">Durée de chaque séance</span>
            <div className="flex items-center gap-1.5">
              <Champ
                type="number" min={0} max={12} libelle="Heures" libelleMasque
                value={String(valeurs.heures)}
                onChange={(e) => setValeurs({ ...valeurs, heures: Number(e.target.value) || 0 })}
              />
              <span className="text-sm text-[var(--color-fg-muted)]">h</span>
              <Champ
                type="number" min={0} max={59} step={5} libelle="Minutes" libelleMasque
                value={String(valeurs.minutes)}
                onChange={(e) => setValeurs({ ...valeurs, minutes: Number(e.target.value) || 0 })}
              />
              <span className="text-sm text-[var(--color-fg-muted)]">min</span>
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Champ
            libelle="Du" type="date" value={valeurs.debut}
            onChange={(e) => setValeurs({ ...valeurs, debut: e.target.value })}
          />
          <Champ
            libelle="Au" type="date" value={valeurs.fin}
            onChange={(e) => setValeurs({ ...valeurs, fin: e.target.value })}
          />
        </div>

        <fieldset className="flex flex-col gap-1.5">
          <legend className="text-[13px] font-medium">Jours de la semaine</legend>
          <div className="flex flex-wrap gap-2">
            {JOURS_OUVRES.map((j) => (
              <CaseACocher
                key={j.numero}
                libelle={j.court}
                checked={valeurs.jours.includes(j.numero)}
                onChange={() => basculer(j.numero)}
              />
            ))}
          </div>
        </fieldset>

        <div className="grid gap-3 sm:grid-cols-2">
          <Champ
            libelle="Intitulé" placeholder="Ex : APC lecture"
            value={valeurs.title}
            onChange={(e) => setValeurs({ ...valeurs, title: e.target.value })}
          />
          <Selecteur
            libelle="Modalité"
            value={valeurs.modality}
            onChange={(e) => setValeurs({ ...valeurs, modality: e.target.value })}
            options={[
            { valeur: '', texte: '—' },
            ...MODALITES.map((m) => ({ valeur: m, texte: m })),
          ]}
          />
        </div>

        <ZoneTexte
          libelle="Description" rows={2}
          value={valeurs.description}
          onChange={(e) => setValeurs({ ...valeurs, description: e.target.value })}
        />

        <CaseACocher
          libelle="Ne rien créer pendant les vacances"
          checked={valeurs.horsVacances}
          onChange={(e) => setValeurs({ ...valeurs, horsVacances: e.target.checked })}
        />

        <p className="text-[12px] text-[var(--color-fg-muted)]">
          Les activités déjà saisies à l’identique ne sont pas recréées : relancer
          la même série ne produit pas de doublons.
        </p>

        <ActionsDialogue>
          <Bouton variante="ghost" onClick={surFermeture}>Annuler</Bouton>
          <Bouton variante="primary" onClick={() => void creer()}>Créer la série</Bouton>
        </ActionsDialogue>
      </div>
    </Dialogue>
  );
}

// ── Objectifs réglementaires ─────────────────────────────────────────────────

function DialogueObjectifs({
  objectifs, surFermeture, surEnregistrement,
}: {
  objectifs: Objectifs;
  surFermeture: () => void;
  surEnregistrement: (corps: Partial<Objectifs>) => Promise<void>;
}) {
  const [valeurs, setValeurs] = useState(objectifs);

  const champ = (cle: keyof Objectifs, libelle: string) => (
    <Champ
      libelle={libelle}
      type="number" min={0} step={0.5}
      value={String(valeurs[cle])}
      onChange={(e) => setValeurs({ ...valeurs, [cle]: Number(e.target.value) || 0 })}
    />
  );

  return (
    <Dialogue
      ouvert
      titre="Objectifs réglementaires"
      description="En heures. Ces valeurs pilotent tous les compteurs."
      surFermeture={surFermeture}
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          {champ('apc', 'APC')}
          {champ('equipe', 'RDV parents, conseils')}
          {champ('formation_animation', 'Formations et animations')}
          {champ('conseil', 'Conseils d’école')}
          {champ('total', 'Total annuel')}
        </div>
        <ActionsDialogue>
          <Bouton variante="ghost" onClick={surFermeture}>Annuler</Bouton>
          <Bouton variante="primary" onClick={() => void surEnregistrement(valeurs)}>
            Enregistrer
          </Bouton>
        </ActionsDialogue>
      </div>
    </Dialogue>
  );
}
